import axios from "axios";
import { getAppParams, getUrlParams } from "./timespam";

// API hosts — php8-migration in dev (.env.development), live hosts in prod (.env.production)
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://goqii2api.goqii.com";
export const APIV4_BASE_URL = process.env.REACT_APP_APIV4_BASE_URL || "https://apiv4.goqii.com";
export const APIV2_BASE_URL = process.env.REACT_APP_APIV2_BASE_URL || "https://apiv2.goqii.com";
export const USERNHS_BASE_URL = process.env.REACT_APP_USERNHS_BASE_URL || "https://usernhs.goqii.com";

const getToken = () => {
  const { goqiiAccessToken } = getAppParams();
  return goqiiAccessToken || localStorage.getItem("authToken") || "";
};

// Original clients — used by games section (no lang)
export const apiClient = axios.create({
  baseURL: `${APIV4_BASE_URL}/lifestylecard`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: false,
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = token;
  const { lang } = getUrlParams();
  config.headers["lang"] = lang;
  return config;
});

export const userClient = axios.create({
  baseURL: `${APIV2_BASE_URL}/user`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: false,
});

userClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = token;
  return config;
});

// New clients — lifestyle APIs (lang in header, params sent in body)
export const newApiClient = axios.create({
  baseURL: `${API_BASE_URL}/lifestylecard`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: false,
});

newApiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = token;
  const { lang, appName } = getUrlParams();
  config.headers["lang"] = lang;
  config.headers["appName"] = appName;
  return config;
});

export const newUserClient = axios.create({
  baseURL: `${API_BASE_URL}/user`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: false,
});

newUserClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = token;
  const { lang } = getUrlParams();
  config.headers["lang"] = lang;
  return config;
});

export const uhtCashClient = axios.create({
  baseURL: `${API_BASE_URL}/goqiiapicoach`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: false,
});

uhtCashClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = token;
  const { lang } = getUrlParams();
  config.headers["lang"] = lang;
  return config;
});

export const challengeClient = axios.create({
  baseURL: `${APIV4_BASE_URL}/activechallenge`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
  withCredentials: false,
});

challengeClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = token;
  const { lang } = getUrlParams();
  config.headers["lang"] = lang;
  return config;
});

export const dermaClient = axios.create({
  baseURL: `${APIV4_BASE_URL}/emotionalmood/`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
  withCredentials: false,
});

dermaClient.interceptors.request.use((config) => {
  const url = new URL(window.location.href);
  const goqiiUserId = url.searchParams.get("goqiiUserId") || url.searchParams.get("goqiiuserid") || "";
  const uuid = url.searchParams.get("uuid") || "";
  const authToken = url.searchParams.get("authToken") || "";
  const uid = url.searchParams.get("uid") || "";
  const apiKey = url.searchParams.get("apiKey") || "";
  const signature = url.searchParams.get("signature") || "";
  const nonce = url.searchParams.get("nonce") || "";
  const goqiiAccessToken = url.searchParams.get("goqiiAccessToken") || "";
  // Authorization is generated from the uuid. authToken is a legacy fallback
  // for links that are still launched with uid/authToken instead of uuid.
  const authorization = uuid || authToken;
  if (authorization) {
    config.headers["Authorization"] = authorization;
    config.headers["uid"] = uid;
    config.headers["goqiiUserId"] = goqiiUserId;
    config.headers["apiKey"] = apiKey;
    config.headers["signature"] = signature;
    config.headers["nonce"] = nonce;
    config.headers["goqiiAccessToken"] = goqiiAccessToken;
  }
  return config;
});

// NHS-only endpoints (WFIRS-S/ADHD) — derma's original app pointed these at
// usernhs.goqii.com when served from derma-nhs.goqii.com (see commonAxios.js),
// unlike the Pfizer/psoriasis endpoints above which live on apiv4.goqii.com.
export const nhsClient = axios.create({
  baseURL: `${USERNHS_BASE_URL}/emotionalmood/`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
  withCredentials: false,
});

nhsClient.interceptors.request.use((config) => {
  const url = new URL(window.location.href);
  const goqiiUserId = url.searchParams.get("goqiiUserId") || url.searchParams.get("goqiiuserid") || "";
  const uuid = url.searchParams.get("uuid") || "";
  const authToken = url.searchParams.get("authToken") || "";
  const uid = url.searchParams.get("uid") || "";
  const apiKey = url.searchParams.get("apiKey") || "";
  const signature = url.searchParams.get("signature") || "";
  const nonce = url.searchParams.get("nonce") || "";
  const goqiiAccessToken = url.searchParams.get("goqiiAccessToken") || "";
  const authorization = uuid || authToken;
  if (authorization) {
    config.headers["Authorization"] = authorization;
    config.headers["uid"] = uid;
    config.headers["goqiiUserId"] = goqiiUserId;
    config.headers["apiKey"] = apiKey;
    config.headers["signature"] = signature;
    config.headers["nonce"] = nonce;
    config.headers["goqiiAccessToken"] = goqiiAccessToken;
  }
  return config;
});

export const pasiClient = axios.create({
  baseURL: `${APIV4_BASE_URL}/emotionalmood/`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
  withCredentials: false,
});

pasiClient.interceptors.request.use((config) => {
  const url = new URL(window.location.href);
  const uuid = url.searchParams.get("uuid") || "";
  const authToken = url.searchParams.get("authToken") || "";
  const uid = url.searchParams.get("uid") || "";
  const goqiiUserId = url.searchParams.get("goqiiUserId") || url.searchParams.get("goqiiuserid") || "";
  const apiKey = url.searchParams.get("apiKey") || "";
  const signature = url.searchParams.get("signature") || "";
  const nonce = url.searchParams.get("nonce") || "";
  const goqiiAccessToken = url.searchParams.get("goqiiAccessToken") || "";
  // Authorization is generated from the uuid — same mechanism as dermaClient.
  // authToken is kept only as a legacy fallback for older links without uuid.
  const authorization = uuid || authToken;
  if (authorization) {
    config.headers["Authorization"] = authorization;
    config.headers["uid"] = uid;
    config.headers["goqiiUserId"] = goqiiUserId;
    config.headers["apiKey"] = apiKey;
    config.headers["signature"] = signature;
    config.headers["nonce"] = nonce;
    config.headers["goqiiAccessToken"] = goqiiAccessToken;
  }
  return config;
});

// Legacy signed API — auth (goqiiUserId/appVersion/appType/goqiiAccessToken/
// nonce/apiKey/signature) travels as querystring params, not headers, same
// scheme the app's own deep links use for this endpoint family.
export const foodInsightsClient = axios.create({
  baseURL: `${APIV4_BASE_URL}/foodinsights`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: false,
});

foodInsightsClient.interceptors.request.use((config) => {
  const { goqiiUserId, appVersion, appType, goqiiAccessToken, nonce, apiKey, signature } = getAppParams();
  config.params = {
    ...config.params,
    goqiiUserId,
    appVersion,
    appType,
    goqiiAccessToken,
    nonce,
    apiKey,
    signature,
  };
  return config;
});

export const genericApiClient = axios.create({
  baseURL: `${API_BASE_URL}/goqiiapigeneric`,
  headers: {
    "Content-Type": "application/json",
    appVersion: "1600",
    appType: "android",
  },
  timeout: 30000,
  withCredentials: false,
});

genericApiClient.interceptors.request.use((config) => {
  // /userlifestyle uses its own "authToken" query param — "goqiiAccessToken"
  // is already used for other purposes on this page.
  const { authToken } = getAppParams();
  const token = authToken || localStorage.getItem("authToken") || "";
  if (token) config.headers.Authorization = token;
  const { lang } = getUrlParams();
  config.headers["lang"] = lang || "en";
  return config;
});
